import { useWaffleFlags } from '@src/data/apiHooks';
import { isCourseKey } from '@src/generic/key-utils';
import React from 'react';

/**
 * Shared context that's used by all our editors.
 *
 * Note: we're in the process of moving things from redux into this.
 */
export interface EditorContext {
  /**
   * The ID of the current course or library.
   * Use `isCourseKey()` from '@src/generic/key-utils' if you need to check what type it is.
   */
  learningContextId: string;
  /** Is the so-called "Markdown" problem editor available in this learning context? */
  isMarkdownEditorEnabledForContext: boolean;
}

export type EditorContextInit = {
  learningContextId: string;
};

const context = React.createContext<EditorContext | undefined>(undefined);

/** Hook to get the editor context (shared context) */
export function useEditorContext() {
  const ctx = React.useContext(context);
  if (ctx === undefined) {
    /* istanbul ignore next */
    throw new Error('This component needs to be wrapped in <EditorContextProvider>');
  }
  return ctx;
}

export const EditorContextProvider: React.FC<{ children: React.ReactNode; } & EditorContextInit> = ({
  children,
  learningContextId,
}) => {
  const courseIdIfCourse = isCourseKey(learningContextId) ? learningContextId : undefined;
  const waffleFlags = useWaffleFlags(courseIdIfCourse);
  const isMarkdownEditorEnabledForContext = waffleFlags.useReactMarkdownEditor;

  const ctx: EditorContext = React.useMemo(() => ({
    learningContextId,
    isMarkdownEditorEnabledForContext,
  }), [
    // Dependencies - make sure we update the context object if any of these values change:
    learningContextId,
    isMarkdownEditorEnabledForContext,
  ]);
  return <context.Provider value={ctx}>{children}</context.Provider>;
};
